import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Avatar,
  Button,
  Card,
  CircularProgress,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";
import { useDataProvider, useNotify, useTranslate } from "react-admin";
import { completeMfaSetup } from "../auth/authProvider";
import Cookies from "universal-cookie";

import { extractBaseDomain } from "../util/domain.helper";

const MfaSetup = () => {
  const [loading, setLoading] = useState(false);
  const [setupData, setSetupData] = useState<{
    secret: string;
    qrCode: string;
    otpAuthUri: string;
  } | null>(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [verifying, setVerifying] = useState(false);

  const dataProvider = useDataProvider();
  const notify = useNotify();
  const translate = useTranslate();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check if MFA token is expired
  const isTokenExpired = (token: string): boolean => {
    try {
      const tokenParts = token.split(".");
      if (tokenParts.length !== 3) {
        return true; // Invalid token format
      }
      const payload = JSON.parse(atob(tokenParts[1]));
      const exp = payload.exp;
      if (!exp) {
        return true; // No expiration, treat as expired for security
      }
      // Check if token is expired (exp is in seconds, Date.now() is in milliseconds)
      const isExpired = Date.now() >= exp * 1000;
      return isExpired;
    } catch (error) {
      return true; // Treat as expired on error
    }
  };

  // Get user ID by decoding the MFA token from cookies
  // This is more secure than storing in localStorage (XSS vulnerability)
  const getUserId = () => {
    try {
      // Get token from cookie
      const cookies = new Cookies();
      const token = cookies.get("token");

      if (!token) {
        console.error("{MfaSetup.getUserId} No token found in cookies");
        return null;
      }

      // Check if token is expired
      if (isTokenExpired(token)) {
        notify("Your session has expired. Please log in again.", {
          type: "error",
        });
        navigate("/login");
        return null;
      }

      // Decode JWT to extract user ID
      const parts = token.split(".");
      if (parts.length !== 3) {
        console.error("{MfaSetup.getUserId} Invalid token format");
        return null;
      }

      const payload = JSON.parse(atob(parts[1]));
      const userId = payload._id;

      if (!userId) {
        console.error("{MfaSetup.getUserId} No user ID in token payload");
        return null;
      }

      // User ID decoded successfully
      return userId;
    } catch (error) {
      console.error("{MfaSetup.getUserId} Failed to decode token");
      return null;
    }
  };

  useEffect(() => {
    // Check if we're coming from login (URL parameter check)
    // This is more secure than localStorage (XSS vulnerability)
    const isSetupRequired = searchParams.get("setup") === "required";
    if (!isSetupRequired) {
      // Not a setup flow, redirect to login
      navigate("/login");
      return;
    }

    const fetchMfaSetup = async () => {
      setLoading(true);
      try {
        const userId = getUserId();
        if (!userId) {
          notify(translate("mfa.setup.user_not_found"), { type: "error" });
          navigate("/login");
          return;
        }

        // Fetching MFA setup data from backend

        // Call POST /admin/users/mfa/setup
        // Note: dataProvider automatically adds /admin prefix
        // userId is extracted from the MFA token on the backend
        const response = await dataProvider.create(
          `users/mfa/setup`,
          {
            data: {},
          },
        );

        // Response received successfully

        // The dataProvider returns the data directly
        // Backend returns: {status: 'success', data: {secret, qrCode, otpAuthUri}}
        // httpClient extracts: {secret, qrCode, otpAuthUri}
        // dataProvider.create returns: {data: {secret, qrCode, otpAuthUri, id, ...}}
        const mfaData = {
          secret: response.data.secret,
          qrCode: response.data.qrCode,
          otpAuthUri: response.data.otpAuthUri,
        };
        // MFA setup data received

        setSetupData(mfaData);
      } catch (error: any) {
        console.error("{MfaSetup} Error:", error);
        notify(error.message || translate("mfa.setup.generate_error"), { type: "error" });
      } finally {
        setLoading(false);
      }
    };

    fetchMfaSetup();

    // Cleanup function: Remove MFA token cookie if user navigates away or closes tab
    // This prevents the token from persisting if setup is not completed
    return () => {
      // Only clean up if MFA setup was not completed successfully
      // We check if we're still on the setup page (not redirected to home)
      if (window.location.hash.includes('/mfa-setup')) {
        const domain = extractBaseDomain(window.location.hostname);
        // Clear the MFA token cookie
        const cookies = new Cookies();
        cookies.remove('token', { path: '/', domain });
        // MFA token cookie removed on unmount
      }
    };
  }, [dataProvider, notify, navigate, searchParams]);

  const handleVerify = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      notify(translate("mfa.setup.enter_6_digit"), { type: "warning" });
      return;
    }

    setVerifying(true);
    try {
      const userId = getUserId();
      if (!userId) {
        notify(translate("mfa.setup.user_not_found"), { type: "error" });
        navigate("/login");
        return;
      }

      // Call POST /admin/users/mfa/verify
      // Note: dataProvider automatically adds /admin prefix
      // userId is extracted from the MFA token on the backend
      const result = await dataProvider.create(`users/mfa/verify`, {
        data: {
          token: verificationCode,
        },
      });

      // MFA verification successful

      // MFA verification successful - extract the NEW token and user data from the response
      // The verify endpoint returns: { data: { success: true, user: {...}, token: "..." } }
      const verifyData = result.data;

      // Check if the response contains the expected data
      // Note: AdminServer-API returns HTTP 200 even for failures (with status: "fail" in body)
      // In that case, verifyData will be an empty object {} because httpClient extracts json.data
      // We need to distinguish between:
      // 1. Invalid MFA code (verifyData is empty) - show error and stay on page
      // 2. Session expired (caught in catch block) - redirect to login
      if (!verifyData || !verifyData.user || !verifyData.token) {
        // This means the verification failed (invalid code)
        // The actual error message was lost because AdminServer-API returns 200 OK
        // So we show a generic "invalid code" message
        notify(translate("mfa.setup.invalid_code"), {
          type: "error",
        });
        return; // Stay on the page, don't redirect to login
      }

      // Extract the NEW full token and user data from the verify response
      const fullToken = verifyData.token;
      const userData = verifyData.user;

      // Token and user data received successfully

      // Prepare complete user data for cookies
      // Note: rep_uname might not be in the MFA verify response, so we set it to undefined
      // The updateJurisdictionsCookie function will handle undefined values gracefully
      const completeUserData = {
        first_name: userData.first_name,
        last_name: userData.last_name,
        jurisdictions: userData.jurisdictions,
        user_name: userData.user_name,
        email: userData.email,
        rep_uname: userData.rep_uname || undefined,
      };
      // User data prepared for cookie storage

      // Complete MFA setup using the same pattern as loginMfa
      // This sets cookies and localStorage, then returns a resolved promise
      await completeMfaSetup(completeUserData, fullToken);
      // Set the hash to home and reload immediately
      // This ensures the first page rendered after reload is the home page with full auth state
      window.location.hash = '#/';
      window.location.reload();

    } catch (error: any) {
      // Verification failed - do NOT store token in cookies
      console.error("{MfaSetup.handleVerify} Error:", error);

      // Extract error message from the error object
      // The error might have the message in error.message or error.body.message
      let errorMessage = translate("mfa.setup.invalid_code");

      if (error.message) {
        errorMessage = error.message;
      } else if (error.body && error.body.message) {
        errorMessage = error.body.message;
      }

      notify(errorMessage, { type: "error" });
      // Keep pending data in localStorage for retry
    } finally {
      setVerifying(false);
    }
  };

  const handleCancel = () => {
    const domain = extractBaseDomain(window.location.hostname);
    // Clear the MFA token cookie
    const cookies = new Cookies();
    cookies.remove('token', {path: '/', domain});

    // Navigate to login
    navigate("/login");
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        alignItems: "center",
        justifyContent: "flex-start",
        background:
          'url(/Untangl_Homepage_Image_1920x1080_V1.jpg)',
        // "url(https://docs-untangl-io.s3-eu-west-1.amazonaws.com/background/Untangl_Homepage_Image_1920x1080_V1.jpg)",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
      }}
    >
      <Card
        sx={{
          minWidth: 400,
          maxWidth: 500,
          marginTop: "3em",
          marginBottom: "3em",
          padding: 2,
        }}
      >
        <Box
          sx={{
            margin: "1em",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Avatar sx={{ bgcolor: "primary.main" }}>
            <SecurityIcon />
          </Avatar>
        </Box>

        <Typography variant="h5" align="center" gutterBottom>
          {translate("mfa.setup.title")}
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", padding: 4 }}>
            <CircularProgress />
          </Box>
        ) : setupData && setupData.qrCode && setupData.secret ? (
          <Box sx={{ padding: 1 }}>
            <Typography
              align="center"
              variant="body2"
              color="textSecondary"
              paragraph
            >
              {translate("mfa.setup.scan_instruction")}
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center", marginY: 0 }}>
              <img
                src={setupData.qrCode}
                alt={translate("mfa.setup.qr_code_alt")}
                style={{ maxWidth: "100%", height: "auto" }}
              />
            </Box>

            <Typography
              align="center"
              variant="body2"
              color="textSecondary"
              paragraph
            >
              {translate("mfa.setup.manual_entry")}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontFamily: "monospace",
                backgroundColor: "#f5f5f5",
                padding: 1,
                borderRadius: 1,
                textAlign: "center",
                marginBottom: 2,
                wordBreak: "break-all",
              }}
            >
              {setupData.secret}
            </Typography>

            <Typography
              align="center"
              variant="body2"
              color="textSecondary"
              paragraph
            >
              {translate("mfa.setup.enter_code")}
            </Typography>

            <TextField
              fullWidth
              label={translate("mfa.setup.verification_code")}
              value={verificationCode}
              onChange={(e) =>
                setVerificationCode(
                  e.target.value.replace(/\D/g, "").slice(0, 6),
                )
              }
              disabled={verifying}
              inputProps={{
                maxLength: 6,
                style: {
                  textAlign: "center",
                  fontSize: "1.5em",
                  letterSpacing: "0.5em",
                },
              }}
              sx={{ marginBottom: 2 }}
            />

            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleVerify}
              disabled={verifying || verificationCode.length !== 6}
              sx={{ marginBottom: 1 }}
            >
              {verifying ? (
                <CircularProgress size={25} thickness={2} />
              ) : (
                translate("mfa.setup.verify_button")
              )}
            </Button>

            <Button variant="outlined" fullWidth onClick={handleCancel}>
              {translate("mfa.setup.cancel_button")}
            </Button>
          </Box>
        ) : (
          <Box sx={{ padding: 2 }}>
            <Typography variant="body2" color="error" align="center">
              {translate("mfa.setup.load_error")}
            </Typography>
            <Typography
              variant="caption"
              color="textSecondary"
              align="center"
              display="block"
              sx={{ marginTop: 1 }}
            >
              {translate("mfa.setup.debug_message")}
            </Typography>
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default MfaSetup;
